import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Nav, RouterLinkEvent } from './shared/nav/nav.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import { Logger } from '../logging/logger';
import { Action } from "./shared/action/action.component";
import { Footer } from "./shared/footer/footer.component";

const DEFAULT_DESCRIPTION = 'Hill Tech Works builds custom web applications, business websites, and integrations for growing businesses. Practical software built to scale.';

export type ActionConfig = {
  heading: string;
  body: string;
  buttonText: string;
  link: string;
};

const DEFAULT_ACTION: ActionConfig = {
  heading: 'Wondering what we can build?',
  body: 'Browse our services to see what we offer.',
  buttonText: 'View services',
  link: '/services',
};

type BreadcrumbItem = { name: string; item: string };

type RouteData = {
  showAction?: boolean;
  action?: ActionConfig;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  noindex?: boolean;
};

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Nav, Action, Footer],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class App {
  private readonly router = inject(Router);
  private readonly logger = inject(Logger);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  protected readonly showAction = signal<boolean>(true);
  protected readonly actionConfig = signal<ActionConfig>(DEFAULT_ACTION);
  protected readonly top = viewChild<ElementRef<HTMLDivElement>>('top');

  private url = '/';

  constructor() {
    this.router.events.pipe(
      takeUntilDestroyed(),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      map(route => {
        const title = route.snapshot.title;

        const fullPath = route.snapshot.pathFromRoot
          .flatMap(r => r.url)
          .map(segment => segment.path)
          .join('/');

        return {
          name: title?.replace(' - Hill Tech Works', ''),
          url: '/' + fullPath
        };
      })
    ).subscribe(({ name, url }) => {
      this.logger.logPageView(name, url);
    });

    this.router.events.pipe(
      takeUntilDestroyed(),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.data;
      }),
      switchMap(data =>
        data.pipe(
          map((d) => d as RouteData)
        )
      )
    ).subscribe(({ showAction, action }) => {
      this.showAction.set(showAction ?? true);
      this.actionConfig.set(action ?? DEFAULT_ACTION);
    });

    this.router.events
      .pipe(takeUntilDestroyed(), filter(event => event instanceof NavigationEnd))
      .subscribe(navEndEvent => {
        this.url = navEndEvent.url;
      });

    this.router.events.pipe(
      takeUntilDestroyed(),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      switchMap(route => route.data.pipe(
        map((data) => ({
          data: data as RouteData,
          title: route.snapshot.title ?? 'Hill Tech Works',
          path: '/' + route.snapshot.pathFromRoot.flatMap(r => r.url).map(s => s.path).filter(Boolean).join('/'),
        }))
      ))
    ).subscribe(({ data, title, path }) => {
      const description = data.description ?? DEFAULT_DESCRIPTION;
      const fullUrl = `https://hilltechworks.com${path}`;

      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:title', content: title });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ property: 'og:url', content: fullUrl });
      this.meta.updateTag({ name: 'twitter:title', content: title });
      this.meta.updateTag({ name: 'twitter:description', content: description });

      let canonical = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!canonical) {
        canonical = this.document.createElement('link');
        canonical.rel = 'canonical';
        this.document.head.appendChild(canonical);
      }
      canonical.href = fullUrl;

      let ldScript = this.document.querySelector<HTMLScriptElement>('script[data-schema="breadcrumb"]');
      if (data.breadcrumbs?.length) {
        const schema = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: data.breadcrumbs.map((crumb, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: crumb.name,
            item: crumb.item,
          })),
        };
        if (!ldScript) {
          ldScript = this.document.createElement('script');
          ldScript.type = 'application/ld+json';
          ldScript.setAttribute('data-schema', 'breadcrumb');
          this.document.head.appendChild(ldScript);
        }
        ldScript.text = JSON.stringify(schema);
      } else if (ldScript) {
        ldScript.remove();
      }

      this.meta.updateTag({ name: 'robots', content: data.noindex ? 'noindex' : 'index,follow' });
    });
  }

  async onRouterLinkClick(event: RouterLinkEvent) {
    const isElementInViewport = this.isElementInViewport(this.top()?.nativeElement);
    const viewportCheck = !isElementInViewport && !!event.shouldDoViewportCheck;

    if (this.url === event.routerLink || viewportCheck) {
      this.top()?.nativeElement.scrollIntoView({
        behavior: 'smooth', block: 'start'
      });
    }
    else {
      await this.router.navigateByUrl(event.routerLink);
    }
  }

  isElementInViewport(element: HTMLDivElement | undefined) {
    if (element === undefined) {
      return false;
    }

    var rect = element.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}
